// Publish revisions (finishing bundle 2A). Henry edits a private working copy
// (scripts.doc_json); clients read the last published copy. These helpers are
// pure so the worker and the editor agree on rounds, labels, what counts as
// "unpublished", block diffs, the suggestion merge and the reminder rules.

import { isBriefTemplate, projectBriefForClient } from './briefFields';
import type { ScriptStatus } from './types';

// ---------- Rounds and labels ----------

/** The status a round lives at. Rounds past three stay `v3` (D6). */
export function statusForRound(round: number): 'v1' | 'v2' | 'v3' {
  if (round >= 3) return 'v3';
  if (round === 2) return 'v2';
  return 'v1';
}

/** Round number implied by a status alone (v1 → 1 ... approved → 3). */
export function roundForStatus(status: ScriptStatus): number | null {
  if (status === 'v1') return 1;
  if (status === 'v2') return 2;
  if (status === 'v3' || status === 'approved') return 3;
  return null;
}

/** The round the client is on, preferring the stored published round. */
export function currentRound(status: ScriptStatus, publishedRound: number | null | undefined): number | null {
  const fromStatus = roundForStatus(status);
  if (status === 'approved') return publishedRound ?? 3;
  if (status === 'v3') {
    return Math.max(3, publishedRound ?? 3);
  }
  return fromStatus;
}

/** "Draft", "Approved" or "Round N". */
export function roundLabel(status: ScriptStatus, publishedRound: number | null | undefined): string {
  if (status === 'draft') return 'Draft';
  if (status === 'approved') return 'Approved';
  return `Round ${currentRound(status, publishedRound) ?? 1}`;
}

// ---------- Block identity ----------

/** JSON with object keys sorted at every depth, so block identity never
 *  depends on the order an editor happened to emit keys in. */
export function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/**
 * The editor fills in attribute defaults the first time it saves a document
 * (textAlign "left", null chip types) and keeps a trailing empty paragraph.
 * A template-built or API-built copy has neither, so comparing raw JSON would
 * report every block as changed. Compare with those removed.
 */
const EDITOR_ATTRIBUTE_DEFAULTS: Record<string, Record<string, unknown>> = {
  paragraph: { textAlign: 'left' }, heading: { textAlign: 'left', level: 1 },
  scriptCell: { kind: 'audio', audioType: null, visualType: null },
  orderedList: { start: 1, type: null }, codeBlock: { language: null },
  briefField: { id: '', key: 'custom', title: '', talent_name: '', audience: 'internal', enabled: false, shoot: null, body_kind: 'rich' },
  link: { target: '_blank', rel: 'noopener noreferrer nofollow', class: null, title: null },
};

function normalizeForCompare(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeForCompare);
  if (!value || typeof value !== 'object') return value;
  const type = (value as { type?: string }).type;
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (key === 'attrs' && child && typeof child === 'object' && !Array.isArray(child)) {
      const attrs: Record<string, unknown> = {};
      for (const [name, attr] of Object.entries(child as Record<string, unknown>)) {
        if (attr === undefined) continue;
        if ((type === 'paragraph' || type === 'heading') && name === 'textAlign' && attr === null) continue;
        if (type && Object.hasOwn(EDITOR_ATTRIBUTE_DEFAULTS[type] ?? {}, name) && attr === EDITOR_ATTRIBUTE_DEFAULTS[type][name]) continue;
        attrs[name] = attr;
      }
      if (Object.keys(attrs).length > 0) out.attrs = attrs;
    } else if (key === 'content' && Array.isArray(child) && child.length === 0) {
      continue;
    } else {
      out[key] = normalizeForCompare(child);
    }
  }
  return out;
}

/** Identity of a block for diffing and merging: key order, editor defaults
 *  and empty content never count as a change. */
export function blockKey(block: unknown): string {
  return stableStringify(normalizeForCompare(block));
}

const EMPTY_PARAGRAPH_KEY = blockKey({ type: 'paragraph' });

/** Blocks without the trailing empty paragraphs the editor keeps. */
function withoutTrailingEmpty(blocks: unknown[]): unknown[] {
  let end = blocks.length;
  while (end > 0 && blockKey(blocks[end - 1]) === EMPTY_PARAGRAPH_KEY) end -= 1;
  return blocks.slice(0, end);
}

/** Whole-document comparison with the same tolerance as blockKey. */
function docKey(doc: unknown): string {
  const root = doc && typeof doc === 'object' ? (doc as { content?: unknown }) : null;
  const content = Array.isArray(root?.content) ? withoutTrailingEmpty(root!.content as unknown[]) : [];
  return content.map(blockKey).join('\n');
}

interface DocRoot {
  type?: unknown;
  content?: unknown;
  [key: string]: unknown;
}

function parseDoc(docJson: string | null | undefined): DocRoot | null {
  if (typeof docJson !== 'string') return null;
  try {
    const doc = JSON.parse(docJson) as unknown;
    if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return null;
    const root = doc as DocRoot;
    return root.type === 'doc' && (root.content === undefined || Array.isArray(root.content)) ? root : null;
  } catch {
    return null;
  }
}

/** Top-level blocks of a ProseMirror doc (rows, headings, paragraphs, brief
 *  fields), or null when the JSON cannot be read. */
export function topLevelBlocks(docJson: string): unknown[] | null {
  const doc = parseDoc(docJson);
  if (!doc) return null;
  return Array.isArray(doc.content) ? doc.content : [];
}

/** Index pairs [a, b] of a longest common subsequence of two key lists. */
function lcsPairs(a: string[], b: string[]): Array<[number, number]> {
  const n = a.length;
  const m = b.length;
  const table: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const pairs: Array<[number, number]> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      pairs.push([i, j]);
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return pairs;
}

export interface BlockChanges {
  changed: number;
  added: number;
  removed: number;
}

/** "4 rows changed, 1 added": unmatched blocks in each gap pair up as changes. */
export function countBlockChanges(fromJson: string, toJson: string): BlockChanges {
  const from = withoutTrailingEmpty(topLevelBlocks(fromJson) ?? []).map(blockKey);
  const to = withoutTrailingEmpty(topLevelBlocks(toJson) ?? []).map(blockKey);
  const pairs = lcsPairs(from, to);
  const result: BlockChanges = { changed: 0, added: 0, removed: 0 };
  let prevA = 0;
  let prevB = 0;
  for (const [a, b] of [...pairs, [from.length, to.length] as [number, number]]) {
    const removed = a - prevA;
    const added = b - prevB;
    const changed = Math.min(removed, added);
    result.changed += changed;
    result.removed += removed - changed;
    result.added += added - changed;
    prevA = a + 1;
    prevB = b + 1;
  }
  return result;
}

// ---------- Three-way merge of a suggestion onto the working copy ----------

export type MergeResult =
  | { ok: true; docJson: string; conflicts: number; conflictBlocks: number[] }
  | { ok: false };

function sameChunk(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Apply the client's suggestion (`theirs`, edited from `base`, the copy they
 * saw) onto Henry's working copy (`ours`), one top-level block at a time.
 * Script rows, headings, paragraphs and brief fields are each one block.
 *
 * diff3 over blocks: base indices unchanged on both sides are anchors; between
 * anchors, whichever side left its chunk alone takes the other side's chunk.
 * Equal-length chunks merge position by position, so Henry editing row 3 and
 * the client editing row 4 never conflict. A real conflict emits the client's
 * chunk and reports where, so the editor can flag "You've changed this row too".
 */
export function mergeDocs(baseJson: string, oursJson: string, theirsJson: string): MergeResult {
  const baseDoc = parseDoc(baseJson);
  const oursDoc = parseDoc(oursJson);
  const theirsDoc = parseDoc(theirsJson);
  if (!baseDoc || !oursDoc || !theirsDoc) return { ok: false };
  const blocks = (doc: DocRoot) => (Array.isArray(doc.content) ? (doc.content as unknown[]) : []);
  // Trailing empty paragraphs are editor furniture: merge without them and
  // give the result Henry's.
  const base = withoutTrailingEmpty(blocks(baseDoc));
  const oursAll = blocks(oursDoc);
  const ours = withoutTrailingEmpty(oursAll);
  const oursTrailing = oursAll.slice(ours.length);
  const theirs = withoutTrailingEmpty(blocks(theirsDoc));
  const baseKeys = base.map(blockKey);
  const oursKeys = ours.map(blockKey);
  const theirsKeys = theirs.map(blockKey);

  const baseToOurs = new Map(lcsPairs(baseKeys, oursKeys));
  const baseToTheirs = new Map(lcsPairs(baseKeys, theirsKeys));
  const anchors: Array<[number, number, number]> = [];
  for (let i = 0; i < base.length; i += 1) {
    const o = baseToOurs.get(i);
    const t = baseToTheirs.get(i);
    if (o !== undefined && t !== undefined) anchors.push([i, o, t]);
  }
  // Anchors must be increasing on every side; LCS pairs are monotone, and
  // filtering a monotone sequence keeps it monotone.
  anchors.push([base.length, ours.length, theirs.length]);

  const out: unknown[] = [];
  const conflictBlocks: number[] = [];
  let conflicts = 0;
  let pb = 0;
  let po = 0;
  let pt = 0;
  const pushConflict = (items: unknown[]) => {
    conflicts += 1;
    for (const item of items) {
      conflictBlocks.push(out.length);
      out.push(item);
    }
  };

  for (const [ab, ao, at] of anchors) {
    const bChunk = baseKeys.slice(pb, ab);
    const oChunk = oursKeys.slice(po, ao);
    const tChunk = theirsKeys.slice(pt, at);
    const oItems = ours.slice(po, ao);
    const tItems = theirs.slice(pt, at);
    if (sameChunk(oChunk, bChunk)) {
      out.push(...tItems);
    } else if (sameChunk(tChunk, bChunk) || sameChunk(oChunk, tChunk)) {
      out.push(...oItems);
    } else if (bChunk.length === oChunk.length && bChunk.length === tChunk.length) {
      for (let k = 0; k < bChunk.length; k += 1) {
        if (oChunk[k] === bChunk[k]) out.push(tItems[k]);
        else if (tChunk[k] === bChunk[k] || oChunk[k] === tChunk[k]) out.push(oItems[k]);
        else pushConflict([tItems[k]]);
      }
    } else {
      pushConflict(tItems);
    }
    if (ab < base.length) out.push(ours[ao]);
    pb = ab + 1;
    po = ao + 1;
    pt = at + 1;
  }

  return {
    ok: true,
    docJson: JSON.stringify({ ...baseDoc, type: baseDoc.type ?? 'doc', content: [...out, ...oursTrailing] }),
    conflicts,
    conflictBlocks,
  };
}

// ---------- What the client would see change ----------

export type UnpublishedKind = 'none' | 'internal' | 'client';

/**
 * Whether the working copy differs from what the client reads. On a brief an
 * edit confined to internal fields is `internal`: publishing still updates the
 * crew's shoot view, but the client sees no difference.
 */
export function publishDiffKind(args: {
  publishedJson: string | null;
  workingJson: string;
  publishedTitle: string | null;
  workingTitle: string;
  templateKey: string;
}): UnpublishedKind {
  if (args.publishedJson === null || args.publishedJson === undefined) return 'client';
  if ((args.publishedTitle ?? '') !== args.workingTitle) return 'client';
  const published = parseDoc(args.publishedJson);
  const working = parseDoc(args.workingJson);
  if (!published || !working) return args.publishedJson === args.workingJson ? 'none' : 'client';
  if (docKey(published) === docKey(working)) return 'none';
  if (!isBriefTemplate(args.templateKey)) return 'client';
  return docKey(projectBriefForClient(published)) === docKey(projectBriefForClient(working))
    ? 'internal'
    : 'client';
}

// ---------- Feedback and reminders ----------

/** The client sent feedback after the current round was published. */
export function feedbackThisRound(
  lastClientFeedbackAt: number | null | undefined,
  roundPublishedAt: number | null | undefined,
): boolean {
  if (lastClientFeedbackAt == null) return false;
  if (roundPublishedAt == null) return true;
  return lastClientFeedbackAt > roundPublishedAt;
}

export const REMIND_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export type ReminderReason = 'ok' | 'not_waiting' | 'no_email' | 'email_off' | 'feedback_in' | 'cooldown';

export interface ReminderState {
  allowed: boolean;
  reason: ReminderReason;
  nextAt: number | null;
}

/** Shared by the Remind client menu item and the worker's refusal. */
export function reminderState(args: {
  status: ScriptStatus;
  archivedAt?: number | null;
  clientEmail: string | null | undefined;
  emailEnabled: boolean;
  lastRemindedAt: number | null | undefined;
  feedbackThisRound: boolean;
  now: number;
}): ReminderState {
  const refuse = (reason: ReminderReason, nextAt: number | null = null): ReminderState => ({
    allowed: false,
    reason,
    nextAt,
  });
  if (args.archivedAt != null || args.status === 'draft' || args.status === 'approved') return refuse('not_waiting');
  if (!args.clientEmail) return refuse('no_email');
  if (!args.emailEnabled) return refuse('email_off');
  if (args.feedbackThisRound) return refuse('feedback_in');
  if (args.lastRemindedAt != null && args.now - args.lastRemindedAt < REMIND_COOLDOWN_MS) {
    return refuse('cooldown', args.lastRemindedAt + REMIND_COOLDOWN_MS);
  }
  return { allowed: true, reason: 'ok', nextAt: null };
}
